import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import { CollegeService, CollegeAreaOfStudyService } from 'us-web-services';
import { ModalBase } from './types';

// -- Education Edit Modal --------------------------------------------------
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MIN_GRADUATION_YEAR = 1900;
const maxGraduationYear = (): number => new Date().getFullYear() + 10;

const validateGraduationYear = (year: string): boolean => {
  if (!year) return true;
  if (!/^\d{4}$/.test(year)) return false;
  const n = Number(year);
  return n >= MIN_GRADUATION_YEAR && n <= maxGraduationYear();
};

// Async typeahead: hit the search endpoint on input; falls back to free-typed entry when the input doesn't match a suggestion.
const TypeaheadSelect = ({
  id,
  placeholder,
  initialValue,
  onSelect,
  fetchSuggestions,
}: {
  id?: string;
  placeholder: string;
  initialValue?: { id?: number; name?: string };
  onSelect: (value?: { id?: number; name: string }) => void;
  fetchSuggestions: (q: string) => Promise<Array<{ id: number; name: string }>>;
}): JSX.Element => {
  const [query, setQuery] = useState(initialValue?.name ?? '');
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{ id: number; name: string }>>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    setQuery(initialValue?.name ?? '');
  }, [initialValue?.id, initialValue?.name]);

  const showMenu = open && suggestions.length > 0;

  useLayoutEffect(() => {
    if (!showMenu || !inputRef.current) return undefined;
    const reposition = (): void => {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuRect({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return (): void => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [showMenu]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query.trim())
        .then((items) => setSuggestions(items.slice(0, 12)))
        .catch(() => setSuggestions([]));
    }, 250);
    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchSuggestions]);

  const choose = (item: { id: number; name: string }): void => {
    setQuery(item.name);
    setOpen(false);
    onSelect(item);
  };

  return (
    <div className='relative w-full'>
      <input
        ref={inputRef}
        id={id}
        type='text'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2'
        placeholder={placeholder}
        value={query}
        onChange={(e): void => {
          setQuery(e.target.value);
          setOpen(true);
          onSelect(undefined);
        }}
        onFocus={(): void => setOpen(true)}
        onBlur={(): void => {
          setTimeout(() => setOpen(false), 150);
        }}
      />
      {showMenu && menuRect && createPortal(
        <ul
          className='fixed z-[1004] bg-white border border-neutral-300 rounded mt-1 max-h-60 overflow-auto shadow'
          style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
        >
          {suggestions.map((item) => (
            <li key={item.id}>
              <button
                type='button'
                className='block w-full text-left px-3 py-2 hover:bg-neutral-100'
                onMouseDown={(): void => choose(item)}
              >
                {item.name}
              </button>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
};

export const EducationEditModal = ({
  isOpen,
  onClose,
  initialEducation,
  onSave,
  onDelete,
}: ModalBase & {
  initialEducation?: {
    id?: number;
    college?: { id?: number; name?: string };
    areaOfStudy?: { id?: number; name?: string };
    graduation?: string;
  };
  onSave: (data: {
    id?: number;
    college: { id?: number; name: string };
    areaOfStudy?: { id?: number; name: string };
    graduationMonth?: string;
    graduationYear?: string;
  }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const initialYear = initialEducation?.graduation
    ? new Date(initialEducation.graduation).getFullYear().toString()
    : '';
  const initialMonth = initialEducation?.graduation
    ? MONTHS_SHORT[new Date(initialEducation.graduation).getMonth()]
    : '';

  const [college, setCollege] = useState<{ id?: number; name: string } | undefined>(
    initialEducation?.college?.name
      ? { id: initialEducation.college.id, name: initialEducation.college.name }
      : undefined,
  );
  const [areaOfStudy, setAreaOfStudy] = useState<
    { id?: number; name: string } | undefined
  >(
    initialEducation?.areaOfStudy?.name
      ? { id: initialEducation.areaOfStudy.id, name: initialEducation.areaOfStudy.name }
      : undefined,
  );
  const [month, setMonth] = useState(initialMonth);
  const [year, setYear] = useState(initialYear);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCollege(
        initialEducation?.college?.name
          ? { id: initialEducation.college.id, name: initialEducation.college.name }
          : undefined,
      );
      setAreaOfStudy(
        initialEducation?.areaOfStudy?.name
          ? {
              id: initialEducation.areaOfStudy.id,
              name: initialEducation.areaOfStudy.name,
            }
          : undefined,
      );
      setMonth(initialMonth);
      setYear(initialYear);
    }
  }, [isOpen, initialEducation, initialMonth, initialYear]);

  const yearValid = validateGraduationYear(year);

  const escapeFilterValue = (s: string): string => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  const searchColleges = async (q: string): Promise<Array<{ id: number; name: string }>> => {
    const safe = escapeFilterValue(q);
    const filter = `name.containsIgnoreCase("${safe}").or(alias.containsIgnoreCase("${safe}"))`;
    const resp = await CollegeService.getByFilter({ params: { q: filter, limit: 12 } });
    return ((resp.data?.data ?? []) as Array<{ id: number; name: string }>).filter(
      (c) => !!c.name,
    );
  };

  const searchStudyAreas = async (
    q: string,
  ): Promise<Array<{ id: number; name: string }>> => {
    const resp = await CollegeAreaOfStudyService.getByFilter({
      params: { name: q, limit: 12 },
    });
    return ((resp.data?.data ?? []) as Array<{ id: number; name: string }>).filter(
      (s) => !!s.name,
    );
  };

  const submit = async (): Promise<void> => {
    if (!college?.id) return;
    if (!yearValid) return;
    setSubmitting(true);
    try {
      await onSave({
        id: initialEducation?.id,
        college,
        areaOfStudy,
        graduationMonth: month || undefined,
        graduationYear: year || undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async (): Promise<void> => {
    if (!initialEducation?.id) return;
    setSubmitting(true);
    try {
      await onDelete(initialEducation.id);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editEducation')}
      className='w-full max-w-2xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          {initialEducation?.id && (
            <Button
              id='delete-sitter-education'
              buttonType={ButtonType.secondary}
              disabled={submitting}
              onClick={clear}
            >
              Clear
            </Button>
          )}
          <Button
            id='save-sitter-education'
            buttonType={ButtonType.primary}
            disabled={!college?.id || !yearValid || submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-12 gap-3 mb-3 items-center'>
        <label className='col-span-4 font-semibold text-base'>College (most recent):</label>
        <div className='col-span-8'>
          <TypeaheadSelect
            id='input-college'
            placeholder={t('pages.sitterProfile.modals.placeholderCollege')}
            initialValue={college}
            onSelect={setCollege}
            fetchSuggestions={searchColleges}
          />
        </div>
      </div>

      <div className='grid grid-cols-12 gap-3 mb-3 items-center'>
        <label className='col-span-4 font-semibold text-base'>Area of study:</label>
        <div className='col-span-8'>
          <TypeaheadSelect
            id='input-study-area'
            placeholder={t('pages.sitterProfile.modals.placeholderMajor')}
            initialValue={areaOfStudy}
            onSelect={setAreaOfStudy}
            fetchSuggestions={searchStudyAreas}
          />
        </div>
      </div>

      <div className='grid grid-cols-12 gap-3 mb-1 items-center'>
        <label htmlFor='select-graduation-month' className='col-span-4 text-base'>
          Graduation date:
        </label>
        <select
          id='select-graduation-month'
          aria-label={t('pages.sitterProfile.modals.expectedGradMonthAriaLabel')}
          className='col-span-4 form-control border border-neutral-300 rounded px-3 py-2'
          value={month}
          onChange={(e): void => setMonth(e.target.value)}
        >
          <option value=''>Month</option>
          {MONTHS_SHORT.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <input
          id='input-graduation-year'
          aria-label={t('pages.sitterProfile.modals.expectedGradYearAriaLabel')}
          type='tel'
          placeholder={t('pages.sitterProfile.modals.placeholderYear')}
          className={`col-span-4 form-control border rounded px-3 py-2 ${
            yearValid ? 'border-neutral-300' : 'border-red-500'
          }`}
          value={year}
          onChange={(e): void => setYear(e.target.value)}
        />
      </div>
      {yearValid ? (
        <p className='text-sm text-right mb-3'>
          If you&apos;re still in school, enter your expected graduation date.
        </p>
      ) : (
        <p className='text-red-600 text-sm text-right mb-3'>
          Enter a valid year between {MIN_GRADUATION_YEAR} and {maxGraduationYear()}
        </p>
      )}
    </Modal>
  );
};
